import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';
import DemoBookingModal from '../../components/feature/DemoBookingModal';

export default function FeaturesPage() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: '产品功能 - 智研云 | 智能数据归集 | 精准费用核算 | 一键生成报表',
    description: '智研云提供智能数据归集、精准费用核算、一键生成报表、智能风险预警、多维数据分析、灵活权限管理等强大功能，全面满足企业研发费用管理需求，提升管理效率。',
    keywords: '产品功能,智能数据归集,费用核算,报表生成,风险预警,数据分析',
    canonical: '/features',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: '智研云产品功能',
      description: '智研云提供智能数据归集、精准费用核算、一键生成报表等强大功能',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '智能项目管理',
          description: '自动识别和归类研发项目，实时跟踪项目进度和费用支出'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '研发工时管理',
          description: '精准记录研发人员工时，自动生成工时报表'
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: '合规风险预警',
          description: '内置数十项涉税风险检测规则，实时监控研发活动合规性'
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: '多口径报表',
          description: '一键生成会计核算、高新认定、加计扣除等多维度专业报表'
        },
        {
          '@type': 'ListItem',
          position: 5,
          name: '资料归档管理',
          description: '自动收集和归档研发过程资料，建立完整的证据链'
        },
        {
          '@type': 'ListItem',
          position: 6,
          name: '人员权限管理',
          description: '灵活的角色权限配置，支持多层级组织架构'
        }
      ]
    }
  });

  const features = [
    {
      icon: 'ri-file-list-3-line',
      title: '智能项目管理',
      description: '自动识别和归类研发项目，实时跟踪项目进度和费用支出，确保每个项目都符合高新技术企业认定标准。',
      details: [
        '项目自动分类与标签管理',
        '实时费用追踪与预算控制',
        '项目进度可视化看板',
        '多维度数据分析报表'
      ]
    },
    {
      icon: 'ri-time-line',
      title: '研发工时管理',
      description: '精准记录研发人员工时，自动生成工时报表，支持多项目工时分配，满足加计扣除政策要求。',
      details: [
        '移动端工时填报',
        '智能工时分配算法',
        '异常工时自动预警',
        '工时统计分析报告'
      ]
    },
    {
      icon: 'ri-shield-check-line',
      title: '合规风险预警',
      description: '内置数十项涉税风险检测规则，实时监控研发活动合规性，提前发现并规避潜在风险。',
      details: [
        '实时风险扫描引擎',
        '智能风险评级系统',
        '合规建议自动推送',
        '历史风险追溯分析'
      ]
    },
    {
      icon: 'ri-file-chart-line',
      title: '多口径报表',
      description: '一键生成会计核算、高新认定、加计扣除、IPO监管等多维度专业报表，满足不同场景需求。',
      details: [
        '会计核算报表自动生成',
        '高新认定材料一键导出',
        '加计扣除申报表智能填写',
        'IPO审计资料快速整理'
      ]
    },
    {
      icon: 'ri-database-2-line',
      title: '资料归档管理',
      description: '自动收集和归档研发过程资料，建立完整的证据链，确保留存备查资料完整合规。',
      details: [
        '文档智能分类归档',
        '电子签章与版本控制',
        '云端安全存储备份',
        '快速检索与导出功能'
      ]
    },
    {
      icon: 'ri-team-line',
      title: '人员权限管理',
      description: '灵活的角色权限配置，支持多层级组织架构，确保数据安全和操作规范。',
      details: [
        '细粒度权限控制',
        '多角色灵活配置',
        '操作日志全程记录',
        '数据脱敏保护机制'
      ]
    }
  ];

  const handleFreeTrial = () => {
    window.open('http://112.74.46.225:8002/admin/login?register=register', '_blank');
  };

  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-50 via-blue-50 to-white py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20technology%20network%20connections%20with%20flowing%20data%20streams%20and%20digital%20nodes%20in%20soft%20teal%20and%20blue%20gradient%20background%20representing%20intelligent%20management%20system%20modern%20minimalist%20style%20high%20tech%20atmosphere&width=1920&height=600&seq=features-hero-bg&orientation=landscape')] opacity-5 bg-cover bg-center"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                <i className="ri-star-fill"></i>
                <span>全方位功能覆盖</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
                强大的<span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">产品功能</span>
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed px-4">
                从项目管理到合规审查，从工时记录到报表生成<br className="hidden sm:block" />
                智研云为您提供研发费用管理的完整解决方案
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm px-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-lg sm:text-xl"></i>
                  <span>6大核心功能模块</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-lg sm:text-xl"></i>
                  <span>20+实用工具</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-lg sm:text-xl"></i>
                  <span>全流程自动化</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="group relative bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-gray-100 hover:border-teal-200 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-500 to-blue-500 rounded-t-xl sm:rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  
                  <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-teal-500 to-blue-500 rounded-xl sm:rounded-2xl mb-5 sm:mb-6 group-hover:scale-110 transition-transform">
                    <i className={`${feature.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 group-hover:text-teal-600 transition-colors">
                    {feature.title}
                  </h3>

                  <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6 leading-relaxed">
                    {feature.description}
                  </p>

                  <div className="space-y-2.5 sm:space-y-3">
                    {feature.details.map((detail, idx) => (
                      <div key={idx} className="flex items-start gap-2 sm:gap-3">
                        <i className="ri-check-line text-teal-600 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-700 text-xs sm:text-sm">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-teal-600 to-blue-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Modern%20abstract%20geometric%20patterns%20with%20flowing%20lines%20and%20dots%20connection%20network%20in%20white%20and%20light%20blue%20tones%20technology%20background%20minimalist%20design&width=1920&height=400&seq=features-cta-bg&orientation=landscape')] opacity-10 bg-cover bg-center"></div>
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center relative z-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 sm:mb-6 px-4">
              立即体验强大功能
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8 px-4">
              开启7天免费试用，无需信用卡，随时可以取消
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
              <button 
                onClick={handleFreeTrial}
                className="w-full sm:w-auto bg-white text-teal-600 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                免费试用
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className="w-full sm:w-auto bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                预约演示
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      
      {/* 预约演示弹窗 */}
      <DemoBookingModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
    </>
  );
}
